/**
 * Guided Goods Receipting action tones — mirrors Invoices Match density.
 *
 * Accept as ordered → Full accept (green)
 * Save receipt      → expand / edit lines / save (amber)
 */

import { isInCreditWorkflow, isReceipted } from './reconciliationHealth'
import { isLineAsOrdered } from './goodsReceiptingAsOrderedHelpers'

/**
 * A receipting line is "resolved" when it has an explicit outcome, either
 * local (ticked As ordered / Report Error with an error chosen) or already
 * saved server-side (Full accept / editType on the receipt record).
 * Unresolved lines are what block completing a receipt.
 */
export function isGrLineOutcomeResolved(
  record = {},
  editedReceipting = [],
  asOrderedLineIds = [],
) {
  if (isLineAsOrdered(asOrderedLineIds, record)) return true
  const errorRow = (editedReceipting || []).find(
    item => String(item?.order ?? '') === String(record?._id ?? record ?? ''),
  )
  if (errorRow && String(errorRow.error || '').trim()) return true
  if ((record?.status || '') === 'Full accept') return true
  if (record?.receipting?.editType) return true
  return false
}

/**
 * Line-outcome progress for a PO: "X of Y lines completed" plus the first
 * unresolved line so the UI can scroll/focus it when completing is attempted.
 */
export function getGrLineOutcomeProgress(
  poLines = [],
  editedReceipting = [],
  asOrderedLineIds = [],
) {
  const lines = Array.isArray(poLines) ? poLines : []
  let firstUnresolvedId = null
  let resolved = 0
  lines.forEach((line, index) => {
    if (isGrLineOutcomeResolved(line, editedReceipting, asOrderedLineIds)) {
      resolved += 1
    } else if (firstUnresolvedId === null) {
      firstUnresolvedId = line?._id ?? `line-${index}`
    }
  })
  return {
    total: lines.length,
    resolved,
    unresolved: lines.length - resolved,
    firstUnresolvedId,
  }
}

export const GR_ACTION_TONE = {
  GREEN: 'green',
  AMBER: 'amber',
  RED: 'red',
}

export const GR_ACTION_TONE_STYLES = {
  green: {
    border: '#52c41a',
    background: '#f6ffed',
    color: '#135200',
    activeBorder: '#389e0d',
    activeBackground: '#d9f7be',
  },
  amber: {
    border: '#faad14',
    background: '#fffbe6',
    color: '#874d00',
    activeBorder: '#d48806',
    activeBackground: '#fff1b8',
  },
  red: {
    border: '#ff4d4f',
    background: '#fff2f0',
    color: '#a8071a',
    activeBorder: '#cf1322',
    activeBackground: '#ffccc7',
  },
}

export const GR_GUIDED_ACTIONS = {
  ACCEPT_AS_ORDERED: {
    key: 'accept_as_ordered',
    label: 'Accept',
    tone: GR_ACTION_TONE.GREEN,
    title: 'Accept as ordered',
    guidance:
      'Everything arrived as ordered — Full accept and save. If any line is short or damaged, use Report Error → Ask for credit? → Save receipt instead.',
  },
  SAVE_RECEIPT: {
    key: 'save_receipt',
    label: 'Save receipt',
    tone: GR_ACTION_TONE.AMBER,
    title: 'Save receipt',
    guidance:
      'Expand the order, mark lines As ordered or Report Error (tick Ask for credit? on shorts), then save. Receipting signs off delivery — it is not the same as invoice reconciled.',
  },
}

export function getGrActionToneStyles(tone = GR_ACTION_TONE.GREEN) {
  return GR_ACTION_TONE_STYLES[tone] || GR_ACTION_TONE_STYLES.green
}

const RECONCILIATION_ERRORS = new Set([
  'Supplier / Invoice adjustment',
  'Weighted item',
])

/** App / GR short reasons that must not be Full-accepted away. */
const SHORT_DELIVERY_REPORT_ERRORS = new Set([
  'Damaged',
  'Not Delivered',
  'Quality Issue',
])

/**
 * True when a loaded line already has an open short / problem report that
 * Accept must not overwrite (use Save receipt + Ask for credit instead).
 * Kept local to avoid a circular import with the app check-in bridge helpers.
 */
export function lineBlocksGrFullAccept(line = {}) {
  const receipting = line?.receipting || {}
  const error = String(receipting.error || line?.error || '').trim()
  if (!error || RECONCILIATION_ERRORS.has(error)) return false

  if (receipting.askForCredit === true) return true
  if (String(receipting.editType || '').trim() === 'credit_note_request') {
    return true
  }

  if (!SHORT_DELIVERY_REPORT_ERRORS.has(error)) {
    // Dashboard draft / other problem reasons still block Accept
    if (String(line?.error || '').trim()) return true
    return false
  }

  const status = String(receipting.status ?? line?.status ?? '').trim()
  if (!status || status === 'Not Saved' || status === 'Sent to Supplier') {
    return true
  }
  if (
    status === 'Sent to supplier' ||
    status === 'CN Requested' ||
    status === 'Rejected by supplier'
  ) {
    return true
  }
  return false
}

export const GR_ACCEPT_BLOCKED_SHORT_COPY =
  'This order has short / problem lines. Expand it, use Report Error and Ask for credit? where needed, then Save receipt — Accept is only for deliveries that arrived as ordered.'

/**
 * Tooltip when Accept is disabled (short reports, drafts, already receipted).
 */
export function getGrAcceptDisabledReason(
  record = {},
  {
    errors = [],
    weightedItems = [],
    products = [],
    asOrderedLineIds = [],
  } = {},
) {
  if (
    !isGrAcceptDisabled(record, {
      errors,
      weightedItems,
      products,
      asOrderedLineIds,
    })
  ) {
    return null
  }

  const supplierId = record?.supplier?._id
  const po = record?.purchaseOrderNumber
  const matchesPo = item => {
    const itemSupplierId = item?.supplier?._id || item?.supplier
    return (
      item?.purchaseOrderNumber === po &&
      String(itemSupplierId) === String(supplierId)
    )
  }

  if ((errors || []).some(matchesPo) || (weightedItems || []).some(matchesPo)) {
    return GR_ACCEPT_BLOCKED_SHORT_COPY
  }

  const loaded = (products || []).find(matchesPo)
  if (loaded?.orders?.some(lineBlocksGrFullAccept)) {
    return GR_ACCEPT_BLOCKED_SHORT_COPY
  }

  return GR_GUIDED_ACTIONS.ACCEPT_AS_ORDERED.guidance
}
/**
 * Whether line edits for this PO are ready to open the save fork modal.
 * As ordered ticks alone are enough; incomplete Report Error rows still block.
 *
 * @param {string[]} [asOrderedLineIds]
 * @param {string[]|null} [poLineIds] - order line ids for this PO (when loaded)
 */
export function hasGrLineEditsReady(
  record = {},
  editedReceipting = [],
  weightedItems = [],
  asOrderedLineIds = [],
  poLineIds = null,
) {
  const supplierId = record?.supplier?._id
  const po = record?.purchaseOrderNumber
  if (!supplierId || !po) return false

  const hasWeighted = (weightedItems || []).some(
    item =>
      item?.purchaseOrderNumber === po &&
      (item.supplier === supplierId || item.supplier?._id === supplierId),
  )
  if (hasWeighted) return true

  const currentErrors = (editedReceipting || []).filter(
    item =>
      item?.purchaseOrderNumber === po &&
      (item.supplier === supplierId || item.supplier?._id === supplierId),
  )
  const errorsComplete =
    !currentErrors.length ||
    currentErrors.every(item => String(item.error || '').trim())

  const asOrderedForPo = (asOrderedLineIds || []).filter(id => {
    if (!Array.isArray(poLineIds)) return true
    return poLineIds.some(lineId => String(lineId) === String(id))
  })

  if (asOrderedForPo.length) {
    return errorsComplete
  }

  if (!currentErrors.length) return false
  return errorsComplete
}

export function hasGrCreditNoteEditsReady(record = {}, editedReceipting = []) {
  const supplierId = record?.supplier?._id
  const po = record?.purchaseOrderNumber
  if (!supplierId || !po) return false

  return (editedReceipting || []).some(item => {
    if (item?.purchaseOrderNumber !== po) return false
    if (item.supplier !== supplierId && item.supplier?._id !== supplierId) {
      return false
    }
    const error = String(item.error || '').trim()
    return Boolean(error) && !RECONCILIATION_ERRORS.has(error)
  })
}

export function hasGrReconciliationEditsReady(
  record = {},
  editedReceipting = [],
  weightedItems = [],
) {
  const supplierId = record?.supplier?._id
  const po = record?.purchaseOrderNumber
  if (!supplierId || !po) return false

  const hasWeighted = (weightedItems || []).some(
    item =>
      item?.purchaseOrderNumber === po &&
      (item.supplier === supplierId || item.supplier?._id === supplierId),
  )
  if (hasWeighted) return true

  return (editedReceipting || []).some(item => {
    if (item?.purchaseOrderNumber !== po) return false
    if (item.supplier !== supplierId && item.supplier?._id !== supplierId) {
      return false
    }
    return RECONCILIATION_ERRORS.has(String(item.error || '').trim())
  })
}

/**
 * Whether Full-accept (Accept) should be disabled.
 * Does not require products to be pre-loaded — Accept fetches on click.
 */
export function isGrAcceptDisabled(
  record = {},
  {
    errors = [],
    weightedItems = [],
    products = [],
    asOrderedLineIds = [],
  } = {},
) {
  const supplierId = record?.supplier?._id
  const po = record?.purchaseOrderNumber
  if (!supplierId || !po) return true

  const matchesPo = item => {
    const itemSupplierId = item?.supplier?._id || item?.supplier
    return (
      item?.purchaseOrderNumber === po &&
      String(itemSupplierId) === String(supplierId)
    )
  }

  if ((errors || []).some(matchesPo)) return true
  if ((weightedItems || []).some(matchesPo)) return true

  const loaded = (products || []).find(matchesPo)
  if (loaded?.orders?.some(lineBlocksGrFullAccept)) {
    return true
  }
  if (
    loaded?.orders?.some(
      line => line.status !== undefined && line.status !== null,
    )
  ) {
    return true
  }

  if (loaded?.orders?.length && (asOrderedLineIds || []).length) {
    const poIds = new Set(loaded.orders.map(line => String(line._id)))
    if ((asOrderedLineIds || []).some(id => poIds.has(String(id)))) {
      return true
    }
  }

  return false
}

/**
 * Line Actions editable for As ordered / Report Error.
 * Hidden/disabled once the line or PO is already receipted / in credit
 * workflow / CN requested (post-Save states — manager finished check-in).
 */
export function canEditGrLineCheckIn(
  line = {},
  parentOrder = {},
  { retrospective = false } = {},
) {
  // Voided stays locked even for retrospective edits.
  if (
    parentOrder?.isVoided === true ||
    Boolean(parentOrder?.voidedAt) ||
    line?.isVoided === true ||
    Boolean(line?.voidedAt)
  ) {
    return false
  }
  if (line?.supplierConfirmed === false) {
    return false
  }
  // Retrospective editing (Admin / Head Chef GM) deliberately reopens
  // post-Save lines; the server re-checks permission on save.
  if (retrospective) {
    return true
  }
  if (isReceipted(parentOrder) || isInCreditWorkflow(parentOrder)) {
    return false
  }
  if ((parentOrder?.receiptingStatus || '') === 'CN Requested') {
    return false
  }
  if ((line?.status || '') === 'Full accept') {
    return false
  }
  return true
}

/**
 * Whether Add product is allowed on this Goods Receipting PO row.
 * Blocked once delivery is receipted or the PO is in credit workflow —
 * adding lines then corrupts aggregate receipting status.
 *
 * @param {object} order - GR table row (uses receiptingStatus)
 * @returns {boolean}
 */
export function canAddProductOnGoodsReceipting(order = {}) {
  if (order?.isVoided === true || Boolean(order?.voidedAt)) {
    return false
  }
  if (isReceipted(order) || isInCreditWorkflow(order)) {
    return false
  }
  return true
}

/**
 * Tooltip when Add product is disabled on a GR row.
 */
export function getAddProductDisabledReason(order = {}) {
  if (order?.isVoided === true || Boolean(order?.voidedAt)) {
    return 'Can’t add products to a voided order.'
  }
  if (isInCreditWorkflow(order)) {
    return 'Can’t add products while this order is in credit workflow.'
  }
  if (isReceipted(order)) {
    return 'Can’t add products after this order is receipted.'
  }
  return null
}

/**
 * Expanded GR line badge for lines created via Add product.
 * @param {{ addedOnReceipting?: boolean }} line
 * @returns {boolean}
 */
export function shouldShowAddedOnReceiptingBadge(line = {}) {
  return Boolean(line?.addedOnReceipting)
}
